import React, { useEffect, useImperativeHandle, useState } from 'react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import useDrawer from '@app/component/overlays/drawer/DrawerRef';
import Drawer from '@app/component/overlays/drawer/Drawer';
import {
	closestCorners,
	DndContext,
	DragOverlay,
	KeyboardSensor,
	PointerSensor,
	useDroppable,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Utilities from '@util/Utilities';
import Hotel from '@app/model/hotel/Hotel';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CompanyHotelService from '@app/model/company_hotel/service/CompanyHotelService';
import { CompanyHotelQueryKeys } from '@app/model/company_hotel/service/CompanyHotelQueryKeys';
import { MarketStateGraphQLQueryKeys } from '@app/model/market_state/service/MarketStateGraphQLQueryKeys';
import { useMarketStateFilterInputTools } from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import CalendarMonth from '@app/state/calendar/CalendarMonth';
import { useReorderCompanyHotelCompetitorsMutation } from '@app/model/company_hotel/service/CompanyHotelServiceHooks';
import Competitor from '@app/model/company_hotel/Competitor';
import CompetitorType from '@app/model/company_hotel/CompetitorType';

type DraggedItem = {
	id: number;
	name: string;
	type: CompetitorType;
};

const mapToDraggedItem = (competitor: Competitor) => {
	return {
		id: competitor.id,
		name: competitor.name,
		type: competitor.type,
	};
};

const ManageCompetitorColumnsOrderDrawer: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	calendarMonth: CalendarMonth
	ref: any
}> = React.forwardRef(({ companyHotel, calendarMonth }, ref: React.Ref<any>) => {

	const drawer = useDrawer();

	const [draggedItem, setDraggedItem] = useState<DraggedItem | undefined>(undefined);
	const [competitors, setCompetitors] = useState<DraggedItem[]>(companyHotel.competitors!.map(c => mapToDraggedItem(c)));

	useEffect(() => {
		setCompetitors(companyHotel.competitors!.map(c => mapToDraggedItem(c)));
	}, [companyHotel.competitors]);


	useImperativeHandle(ref, () => {
		return {
			open: () => {
				drawer.open();
			},
		};
	});

	const queryClient = useQueryClient();
	const { filter } = useMarketStateFilterInputTools();

	const reorderCompanyHotelCompetitorsMutation = useReorderCompanyHotelCompetitorsMutation({
		onSuccess: () => {
			queryClient.refetchQueries({
				queryKey: MarketStateGraphQLQueryKeys.details(companyHotel.id, calendarMonth.index.year, calendarMonth.index.month, filter),
				type: 'active',
			});
			queryClient.invalidateQueries({ queryKey: MarketStateGraphQLQueryKeys.details(companyHotel.id, calendarMonth.index.year, calendarMonth.index.month, filter) });
		},
	});

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const { setNodeRef } = useDroppable({
		id: 'id',
	});

	const handleDragStart = (event: any) => {
		const { active } = event;

		const item = competitors.find(arr => arr.id == active.id);
		setDraggedItem(item);
	};

	const handleDragEnd = (event: any) => {
		const { active, over } = event;

		if (over && active.id !== over.id) {
			const oldIndex = competitors.findIndex(i => '' + i.id === active.id);
			const newIndex = competitors.findIndex(i => '' + i.id === over.id);
			const sortedItems = arrayMove(competitors, oldIndex, newIndex);

			reorderCompanyHotelCompetitorsMutation.mutate({
				companyHotelId: companyHotel.id,
				competitors: sortedItems.map(c => {
					return {
						id: c.id,
						type: c.type,
					};
				}),
			});

			setCompetitors(sortedItems);

		}

		setDraggedItem(undefined);
	};

	return <Drawer title="Manage Filters" ref={drawer.ref}>
		<DndContext collisionDetection={closestCorners}
								onDragStart={handleDragStart}
								onDragEnd={handleDragEnd}
								sensors={sensors}
								onDragAbort={() => setDraggedItem(undefined)}
								onDragCancel={() => setDraggedItem(undefined)}
		>
			<div className="mt-2 px-6 py-5 space-y-6">
				<p className="font-semibold text-sm tracking-tight text-gray-900">You can rearrange the order of the competitors
					shown in the tables</p>
				<div
					className="divide-y divide-gray-200 overflow-hidden rounded-md bg-gray-50 ring-1 ring-black ring-opacity-10">
					<div className="px-4 py-3"><h6 className="text-sm font-semibold text-gray-900">Competitors</h6></div>
					<SortableContext id={'id'} items={competitors.map(competitor => '' + competitor.id) || []}
													 strategy={verticalListSortingStrategy}>
						<div ref={setNodeRef} className="px-4 py-5 space-y-2 min-h-[300px]">
							{competitors.map(competitor => <RenderFilterElement key={competitor.id} item={competitor}
																																	opacity={!!draggedItem && draggedItem.id === competitor.id ? 0.5 : 1} />)}
							<DragOverlay>{draggedItem ? <RenderFilterElement item={draggedItem} withShadow /> : null}</DragOverlay>

						</div>
					</SortableContext>
				</div>
			</div>

		</DndContext>
	</Drawer>;
});

const RenderFilterElement: React.FunctionComponent<{
	item: Hotel,
	opacity?: number,
	withShadow?: boolean

}> = ({ item, opacity = 1, withShadow = false }) => {

	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
	} = useSortable({ id: '' + item.id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity,
		boxShadow: withShadow ? ' 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' : 'none',
	};

	return <div id={'' + item.id} ref={setNodeRef} style={style}
							className={Utilities.classNames('flex items-center justify-between rounded-md px-3 py-2 ring-1 ring-black ring-opacity-5 text-xs', 'bg-white')}>
		<span className="flex items-center">
			<button {...attributes} {...listeners}>
				<img
					className="block h-5 w-auto cursor-grab mr-2"
					src="/assets/img/grid-dots.svg"
					alt="RoomPulse"
				/>
			</button>
			<span className="font-medium, text-gray-700">
				{item.name}
			</span>
		</span>
	</div>;
};

export default ManageCompetitorColumnsOrderDrawer;